import { useState, useEffect } from 'react'
import { MoreHorizontal, PlusCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useAuth } from '@/contexts/auth-context'
import { supabase } from '@/utils/supabase/client'
import { InventoryCategory } from '@/types'
import { CategoryFormModal } from './category-form-modal'
import { ConfirmationDialog } from './confirmation-dialog'
// TODO: Create CategoryFormModal

export function CategoryList() {
  const [categories, setCategories] = useState<InventoryCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<InventoryCategory | null>(null)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const { currentOrganization } = useAuth()

  useEffect(() => {
    if (currentOrganization) {
      fetchCategories()
    }
  }, [currentOrganization])

  const fetchCategories = async () => {
    if (!currentOrganization) return
    setLoading(true)
    const { data } = await supabase
      .from('inventory_categories')
      .select('*')
      .eq('organization_id', currentOrganization.id)
    
    if (data) setCategories(data)
    setLoading(false)
  }

  const handleSave = () => {
    fetchCategories();
  }

  const handleEdit = (category: InventoryCategory) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  }

  const handleDelete = (category: InventoryCategory) => {
    setSelectedCategory(category);
    setIsConfirmOpen(true);
  }

  const confirmDelete = async () => {
    if (!selectedCategory) return;
    await supabase.from('inventory_categories').delete().eq('id', selectedCategory.id)
    fetchCategories();
    setIsConfirmOpen(false);
    setSelectedCategory(null);
  }

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => {
          setSelectedCategory(null);
          setIsModalOpen(true);
        }}>
          <PlusCircle className="h-4 w-4 mr-2" />
          Add Category
        </Button>
      </div>
       {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead><span className="sr-only">Actions</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.map(category => (
              <TableRow key={category.id}>
                <TableCell className="font-medium">{category.name}</TableCell>
                <TableCell>{category.description ?? 'N/A'}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button aria-haspopup="true" size="icon" variant="ghost">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Toggle menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuItem onSelect={() => handleEdit(category)}>Edit</DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => handleDelete(category)}>Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <CategoryFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        category={selectedCategory}
      />
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Are you sure?"
        description="This action cannot be undone. This will permanently delete the category."
      />
    </>
  )
} 